import React, { FC, useContext, useReducer, useState, Suspense, useEffect } from 'react';
import SecureContent, { AuthenticationResult } from 'src/core/services/authentication';
import { AppConfig, container } from 'src/inversify.config';
import Shell, { setHeader, setUsername } from 'src/core/ui/shell';
import { Button, Col, Menu, Row, Spin, Tooltip } from 'antd';
import {
  UserOutlined,
  AuditOutlined,
  TeamOutlined,
  IdcardOutlined,
  BankOutlined,
  LogoutOutlined,
  LockOutlined,
  LoadingOutlined,
  FileProtectOutlined,
  MonitorOutlined,
  FileDoneOutlined,
  KeyOutlined,
  FileTextOutlined,
  ReadOutlined,
  LineChartOutlined,
  FundOutlined,
  SunFilled,
  MoonFilled,
  LaptopOutlined,
  MobileOutlined,
  CalendarOutlined,
  YoutubeOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PermissionService from 'src/common/permission-service';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { PermissionsConstants } from 'src/common/types';
import i18next from 'i18next';
import logo from 'src/core/assets/images/SPT_Logo Full_2end_Digital_White-Red.png';
import smallLogo from 'src/core/assets/images/Small-logo-Red2.png';
import useMediaQuery from 'src/common/hooks/use-media-query';
import {
  OrganizationExportImportContext,
  organizationExportImportReducer,
  organizationExportImportContextInitialData,
} from './organizations/organization-export-context';
import { Route, Link, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { ThemeContext } from 'src';
import { UserItemStore, UserProfileItemStore } from 'src/stores/users-store';
import { useThemeDetector } from 'src/common/hooks/use-theme-detector';
import { ItemType } from 'antd/es/menu/interface';
import { Theme, ThemeSelection } from 'src/common/themes';
import LanguageSelect from 'src/common/components/business/language-select';
import ClientsListPage from './clients/client-list';
import HomePage from './home';
import OrganizationsPage from './organizations';
import UsersPage from './users';
import RolesPage from './roles';
import AdminRolesPage from './admin-roles';
import DefaultRolesPage from './default-roles';
import GroupsPage from './groups';
import PermissionsPage from './permissions';
import RoleAssignmentsPage from './role-assignments';
import AuditLogsPage from './audit-logs';
import LicensesPage from './licenses';
import StatisticsPage from './statistics';
import UnauthorizedPage from './unauthorized';
import NotFoundPage from './not-found';
import EulaPage from './eula';
import DevLogsPage from './dev-logs';
import HangfireDashboard from './hangfire-dashboard';
import ScheduledEventsListPage from './scheduled-events/scheduled-event-list';
import { EventType, ScheduledEventItemStore, ScheduledEventSummaryStore } from 'src/stores/scheduled-events-store';
import { Languages } from 'src/i18n';
import moment from 'moment';
import { Query } from 'src/core/stores/data-store';
import { getDateFormat } from 'src/common/utils';
import { Notifications } from 'src/pages/notifications/notifications';
import { useSessionNotifications } from 'src/pages/notifications/notifications-session-store';
import { NotificationsReceiver } from 'src/pages/notifications/notifications-receiver';
import TutorialsListPage from './manage-tutorials/tutorial-list';
import NotificationsTableView from './notifications/notifications-table';
import { create } from 'zustand';
import { NotificationsContext } from './notifications/notifications-context';

const permissions = [
  PermissionsConstants.CreateOrganization,
  PermissionsConstants.CreatePartnerOrganization,
  PermissionsConstants.DeleteOrganization,
  PermissionsConstants.DeletePartnerOrganization,
  PermissionsConstants.ManageOrganizationInfo,
  PermissionsConstants.ManageAdminOrganization,

  PermissionsConstants.ManageUsers,
  PermissionsConstants.ManageRoles,
  PermissionsConstants.ManageGroups,
  PermissionsConstants.ManageDefaultRoles,
  PermissionsConstants.ManagePermissions,
  PermissionsConstants.ManageRoleAssignments,
  PermissionsConstants.ManageGlobalLicenses,
  PermissionsConstants.CanManageClients,
  PermissionsConstants.ManageVideoTutorialData,
  PermissionsConstants.ViewAllOrganizations,
];

export const useSideMenu = create<{ selectedSideMenu: string; setSelectedSideMenu: (selected: string) => void }>((set) => ({
  selectedSideMenu: '',
  setSelectedSideMenu: (selected) => set({ selectedSideMenu: selected }),
}));

const AdminIndex: FC = () => {
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const permissionSvc = container.get(PermissionService);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const selectedKey = pathname.split('/')[1];
  const { selectedSideMenu: selectedMenu, setSelectedSideMenu: setSelectedMenu } = useSideMenu();
  const [authResult, setAuthResult] = useState<AuthenticationResult>();
  const [unauthorized, setUnauthorized] = useState<boolean>(false);
  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(authResult, permissions);
  const matches = useMediaQuery('(max-width: 768px)');
  const [collapsed, setCollapsed] = useState(matches);
  const [organizationExportState, organizationExportDispatch] = useReducer(
    organizationExportImportReducer,
    organizationExportImportContextInitialData
  );
  const { themeData, setThemeData } = useContext(ThemeContext);
  const userStore = container.get(UserItemStore);
  const systemUsesDarkTheme = useThemeDetector();

  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const userProfileStore = container.get(UserProfileItemStore);
  const userProfileState = userProfileStore.state;

  const scheduledEventsSummaryStore = container.get(ScheduledEventSummaryStore);
  const scheduledEventsSummaryState = scheduledEventsSummaryStore.state;
  const scheduledEventsItemStore = container.get(ScheduledEventItemStore);
  const scheduledEventsItemState = scheduledEventsItemStore.state;
  const [banner, setBanner] = useState<{ img?: string; link?: string; alt?: string }>();
  const [alert, setAlert] = useState<string>();
  const [notificationsVisible, setNotificationsVisible] = useState(false);
  const [notificationsRevision, setNotificationsRevision] = useState(0);
  const sessionNotifications = useSessionNotifications();

  const updateEventBanners = async (language: Languages) => {
    const now = moment(new Date()).toISOString();
    const activeEventsQuery: Partial<Query> = {
      filter: [`announcementStartDate le ${now} and announcementEndDate ge ${now}`],
      orderBy: [{ field: 'announcementStartDate', direction: 'Ascending', useProfile: false }],
    };
    await scheduledEventsSummaryStore.load(activeEventsQuery);
    const scheduledUpdate = scheduledEventsSummaryState.items.value.find((v) => v.type === EventType.ScheduledUpdate);
    const announcement = scheduledEventsSummaryState.items.value.find((v) => v.type === EventType.Announcement);
    if (scheduledUpdate) {
      const startDate = moment(scheduledUpdate.scheduledUpdateStart).locale(language).format(getDateFormat());
      const startMoment = moment(scheduledUpdate.scheduledUpdateStart).locale(language).format('hh:mm a');
      const hours = scheduledUpdate.announcementDuration;
      const alertText = t('ScheduledMaintenanceText').replace('{0}', startDate).replace('{1}', `${hours}`).replace('{2}', startMoment);
      setAlert(alertText);
    }
    if (announcement) {
      await scheduledEventsItemStore.loadWithSelectedLanguages(announcement.id, language);
      if (scheduledEventsItemState.item.value.announcementInfo.length > 0) {
        const announcementData = scheduledEventsItemState.item.value;
        const localizedData = announcementData.announcementInfo[0];
        setBanner({
          alt: localizedData.text,
          img: localizedData.image,
          link: announcementData.announcementLink,
        });
      }
    }
  };

  const getName = () => {
    return `${authResult?.profile['display_name'] ?? ''}`.trimStart().trimEnd();
  };

  useEffect(() => {
    setHeader(t('WelcomeBack').replace('{0}', `${getName()}`));
    setUsername(getName());

    if (authResult) {
      setSelectedMenu(selectedKey || '/');
    }
  }, [authResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateTheme = (userTheme: ThemeSelection, updateUserData = true) => {
    switch (userTheme) {
      case ThemeSelection.Auto: {
        const theme = systemUsesDarkTheme ? Theme.Dark : Theme.Light;
        setThemeData(() => ({
          detectSystem: true,
          selectedTheme: theme,
        }));
        break;
      }
      case ThemeSelection.Light: {
        setThemeData(() => ({
          detectSystem: false,
          selectedTheme: Theme.Light,
        }));
        break;
      }
      case ThemeSelection.Dark: {
        setThemeData(() => ({
          detectSystem: false,
          selectedTheme: Theme.Dark,
        }));
      }
    }

    if (updateUserData) {
      userStore.updatePreferredTheme(authResult?.profile['sub'], userTheme);
    }
  };
  const headerMenuEntries: ItemType[] = [];

  if (!checkingPermissions && authResult) {
    headerMenuEntries.push({
      key: 'language-dropdown',
      label: `${t('Language')}:`,
      type: 'group',
      children: [
        {
          key: 'language',
          type: 'item',
          label: (
            <div
              className="dropdown-option-menu"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <LanguageSelect
                stopEventPropagation
                allowClear={false}
                value={userProfileState?.item?.value?.defaultLanguage ?? authResult?.profile['default_lang']}
                onChange={async (v) => {
                  const postData = {
                    defaultLanguage: v,
                    displayName: authResult?.profile['display_name'],
                    email: authResult.email!,
                  };
                  setHeaderMenuOpen(false);
                  let response = await userProfileStore.save(authResult?.profile['sub'], postData as any);
                  if (response?.isSuccess) {
                    updateEventBanners(v as Languages);
                    i18next.changeLanguage(v);
                    document.cookie = `language=${v}`;
                    userProfileState.set((s) => {
                      s = {
                        ...s,
                        item: {
                          ...s?.item,
                          defaultLanguage: v,
                          displayName: authResult?.profile['display_name'],
                          email: authResult.email!,
                          id: authResult.profile['sub'],
                        },
                      };
                      return s;
                    });
                  }
                }}
              />
            </div>
          ),
        },
      ],
    });

    headerMenuEntries.push({
      key: 'theme-menu-dropdown',
      label: `${t('Appearance')}:`,
      type: 'group',
      children: [
        {
          key: 'theme-selection',
          type: 'item',
          label: (
            <Row style={{ width: '80%', margin: 'auto' }}>
              <Col span={8}>
                <Tooltip title={t('LightTheme')}>
                  <Button
                    className={`solid-button ${themeData.selectedTheme === Theme.Light && !themeData.detectSystem ? 'theme-button-active' : ''}`}
                    type="dashed"
                    onClick={() => updateTheme(ThemeSelection.Light)}
                  >
                    <SunFilled />
                  </Button>
                </Tooltip>
              </Col>
              <Col span={8}>
                <Tooltip title={t('DarkTheme')}>
                  <Button
                    className={`solid-button ${themeData.selectedTheme === Theme.Dark && !themeData.detectSystem ? 'theme-button-active' : ''}`}
                    type="dashed"
                    onClick={() => updateTheme(ThemeSelection.Dark)}
                  >
                    <MoonFilled />
                  </Button>
                </Tooltip>
              </Col>
              <Col span={8}>
                <Tooltip title={t('UseSystemTheme')}>
                  <Button
                    className={`solid-button ${themeData.detectSystem ? 'theme-button-active' : ''}`}
                    type="dashed"
                    onClick={() => updateTheme(ThemeSelection.Auto)}
                  >
                    <LaptopOutlined />
                  </Button>
                </Tooltip>
              </Col>
            </Row>
          ),
        },
      ],
    });

    headerMenuEntries.push({
      type: 'divider',
    });

    if (authResult?.email === 'admin@tempuri.org' || authResult?.email === 'melissa.alonso@sptab.com') {
      headerMenuEntries.push({
        label: t('HangfireDashboard'),
        key: 'hangfire',
        icon: <LineChartOutlined />,
        onClick: () => navigate('/export-import-hangfire-dashboard'),
      });
    }

    headerMenuEntries.push({
      label: t('Logout'),
      key: 'logout',
      icon: <LogoutOutlined />,
      onClick: () => authResult?.logout(),
    });
  }

  return (
    <SecureContent
      config={{
        authority: config!.identityServerUrl,
        redirectUri: `${config!.hostUrl}${process.env.PUBLIC_URL}`,
        silentRedirectUri: `${config!.hostUrl}${process.env.PUBLIC_URL}/silentrefresh.html`,
        resource: config!.scopes,
        clientId: config!.clientId,
        passthrough: config!.passthrough,
      }}
      onAuthentication={(result) => {
        setAuthResult(result);
        updateEventBanners(result?.profile['default_lang']);
        updateTheme(result.profile['theme'], false);
        if (result && result.error == null && result.profile['is_spt_user'] === 'true') {
          i18next.changeLanguage(result?.profile['default_lang']);
          setUnauthorized(false);
          permissionSvc.setCurrentUser(result.profile['sub'], result.profile['tenant_id']);
          window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
        } else {
          setUnauthorized(true);
          permissionSvc.clearCurrentUser();
          navigate(`unauthorized`);
        }
      }}
    >
      {checkingPermissions ? (
        <Spin className="global-spinner" indicator={<LoadingOutlined style={{ fontSize: 50 }} spin />} size="large"></Spin>
      ) : (
        <div className={`shell-wrapper ${collapsed ? '' : 'hide-layout'}`}>
          <OrganizationExportImportContext.Provider value={{ organizationExportState, organizationExportDispatch }}>
            <NotificationsReceiver />
            <Shell
              notificationsVisible={notificationsVisible}
              onNotificationsVisibilityChange={(visible) => {
                setNotificationsVisible(() => visible);
                setNotificationsRevision((prev) => prev + 1);
              }}
              notificationsCount={sessionNotifications.state.unreadNotifications.count}
              notificationsComponent={<Notifications updateVisibility={setNotificationsVisible} revision={notificationsRevision} />}
              warning={alert}
              banner={banner?.img}
              bannerLink={banner?.link}
              bannerAlt={banner?.alt}
              headerMenu={{
                className: 'header-menu',
                items: headerMenuEntries,
              }}
              headerMenuDropdownOpen={headerMenuOpen}
              setHeaderMenuDropdownOpen={setHeaderMenuOpen}
              logo={logo}
              smallLogo={smallLogo}
              menu={
                <Menu
                  mode="inline"
                  className={collapsed ? 'spt-collapsed-sidebar' : ''}
                  defaultSelectedKeys={[]}
                  selectedKeys={[selectedMenu]}
                  onClick={({ key }) => setSelectedMenu(key as string)}
                >
                  {!unauthorized && authResult && (
                    <>
                      {userPermissionsSet.has(PermissionsConstants.ViewAllOrganizations) && (
                        <Menu.Item key="organizations" icon={<BankOutlined className={collapsed ? 'collapsed-icon' : ''} />}>
                          <Link to={`organizations`}>{t('Organizations')}</Link>
                        </Menu.Item>
                      )}
                      {userPermissionsSet.has(PermissionsConstants.ManageAdminOrganization) && (
                        <Menu.Item
                          className={collapsed ? '' : 'menu-item-link'}
                          key="scheduled-events"
                          icon={<CalendarOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                        >
                          <Link to={`scheduled-events`}>{t('ScheduledEvents')}</Link>
                        </Menu.Item>
                      )}
                      {userPermissionsSet.has(PermissionsConstants.ManageVideoTutorialData) && (
                        <Menu.Item
                          className={collapsed ? '' : 'menu-item-link'}
                          key="video-tutorials"
                          icon={<YoutubeOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                        >
                          <Link to={`video-tutorials`}>{t('VideoTutorials')}</Link>
                        </Menu.Item>
                      )}
                      {userPermissionsSet.has(PermissionsConstants.ManageUsers) && (
                        <Menu.Item
                          key="users"
                          className={collapsed ? '' : 'menu-item-link'}
                          icon={<UserOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                        >
                          <Link to={`users`}>{t('Users')}</Link>
                        </Menu.Item>
                      )}
                      {userPermissionsSet.has(PermissionsConstants.ManageRoles) && (
                        <Menu.Item
                          key="roles"
                          className={collapsed ? '' : 'menu-item-link'}
                          icon={<AuditOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                        >
                          <Link to={`roles`}>{t('Roles')}</Link>
                        </Menu.Item>
                      )}
                      {userPermissionsSet.has(PermissionsConstants.ManageRoles) && (
                        <Menu.Item
                          key="adminroles"
                          className={collapsed ? '' : 'menu-item-link'}
                          icon={<FileProtectOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                        >
                          <Link to={`adminroles`}>{t('AdminRoles')}</Link>
                        </Menu.Item>
                      )}
                      {userPermissionsSet.has(PermissionsConstants.ManageGroups) && (
                        <Menu.Item
                          key="groups"
                          className={collapsed ? '' : 'menu-item-link'}
                          icon={<TeamOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                        >
                          <Link to={`groups`}>{t('Groups')}</Link>
                        </Menu.Item>
                      )}
                      {userPermissionsSet.has(PermissionsConstants.ManageDefaultRoles) && (
                        <Menu.Item
                          key="default-roles"
                          className={collapsed ? '' : 'menu-item-link'}
                          icon={<FileDoneOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                        >
                          <Link to={`default-roles`}>{t('DefaultRoles')}</Link>
                        </Menu.Item>
                      )}
                      {userPermissionsSet.has(PermissionsConstants.ManagePermissions) && (
                        <Menu.Item
                          key="permissions"
                          className={collapsed ? '' : 'menu-item-link'}
                          icon={<LockOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                        >
                          <Link to={`permissions`}>{t('Permissions')}</Link>
                        </Menu.Item>
                      )}
                      {(userPermissionsSet.has(PermissionsConstants.ManageRoleAssignments) ||
                        userPermissionsSet.has(PermissionsConstants.ManageGlobalLicenses)) && (
                        <Menu.Item
                          key="role-assignments"
                          className={collapsed ? '' : 'menu-item-link'}
                          icon={<IdcardOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                        >
                          <Link to={`role-assignments`}>{t('Assignments')}</Link>
                        </Menu.Item>
                      )}
                      {userPermissionsSet.has(PermissionsConstants.ManageUsers) && (
                        <Menu.Item
                          className={collapsed ? '' : 'menu-item-link'}
                          key="audit-logs"
                          icon={<MonitorOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                        >
                          <Link to={`audit-logs`}>{t('AuditLogs')}</Link>
                        </Menu.Item>
                      )}
                      {authResult.email === 'admin@tempuri.org' && (
                        <Menu.Item
                          key="import-export-dev-logs"
                          className={collapsed ? '' : 'menu-item-link'}
                          icon={<FileTextOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                        >
                          <Link to={`dev-logs`} className="menu-item-long-text">
                            {t('DevLogsShort')}
                          </Link>
                        </Menu.Item>
                      )}
                      {userPermissionsSet.has(PermissionsConstants.ManageGlobalLicenses) && (
                        <Menu.Item
                          key="licensing"
                          className={collapsed ? '' : 'menu-item-link'}
                          icon={<KeyOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                        >
                          <Link to={`licenses`}>{t('Licenses')}</Link>
                        </Menu.Item>
                      )}
                      {[PermissionsConstants.ManageAdminOrganization].some((x) => userPermissionsSet.has(x)) && (
                        <Menu.Item
                          key="statistics"
                          className={collapsed ? '' : 'menu-item-link'}
                          icon={<FundOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                        >
                          <Link to={`statistics`}>{t('Statistics')}</Link>
                        </Menu.Item>
                      )}
                      {userPermissionsSet.has(PermissionsConstants.CanManageClients) && (
                        <Menu.Item
                          key="clients"
                          className={collapsed ? '' : 'menu-item-link'}
                          icon={<MobileOutlined className={collapsed ? 'collapsed-icon' : ''} />}
                        >
                          <Link to={`clients`}>{t('Clients')}</Link>
                        </Menu.Item>
                      )}
                    </>
                  )}
                </Menu>
              }
              logout={
                <Menu mode="inline" defaultSelectedKeys={[]} selectedKeys={[selectedMenu]} onClick={({ key }) => setSelectedMenu(key as string)}>
                  <Menu.Item className={collapsed ? '' : 'eula-link'} icon={<ReadOutlined style={{ display: collapsed ? 'inherit' : 'none' }} />}>
                    <Link to={`eula`}>{t('EULATitle')}</Link>
                  </Menu.Item>

                  <div
                    style={{
                      color: '#89909a',
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      marginBottom: '20px',
                      marginLeft: '5px',
                      fontSize: 'small',
                      marginTop: '15px',
                    }}
                  >
                    <span>v{config.appVersion}</span>
                  </div>
                </Menu>
              }
              collapsed={collapsed}
              onCollapse={() => setCollapsed(!collapsed)}
              i18nConfig={{
                languages: ['en', 'es', 'pt', 'fr', 'ru'],
                defaultLanguage: 'en',
              }}
            >
              {unauthorized ? (
                <Suspense fallback={<Spin spinning />}>
                  <Routes>
                    <Route element={<UnauthorizedPage />} />
                  </Routes>
                </Suspense>
              ) : (
                <NotificationsContext>
                  <Suspense fallback={<Spin spinning />}>
                    <Routes>
                      <Route path={`/*`} element={<HomePage />} />

                      <Route
                        path={`organizations/*`}
                        element={userPermissionsSet.has(PermissionsConstants.ViewAllOrganizations) ? <OrganizationsPage /> : <UnauthorizedPage />}
                      />
                      <Route path={`notifications/*`} element={<NotificationsTableView />} />
                      <Route
                        path={`users/*`}
                        element={userPermissionsSet.has(PermissionsConstants.ManageUsers) ? <UsersPage /> : <UnauthorizedPage />}
                      />
                      <Route
                        path={`roles/*`}
                        element={userPermissionsSet.has(PermissionsConstants.ManageRoles) ? <RolesPage /> : <UnauthorizedPage />}
                      />
                      <Route
                        path={`adminroles/*`}
                        element={userPermissionsSet.has(PermissionsConstants.ManageRoles) ? <AdminRolesPage /> : <UnauthorizedPage />}
                      />
                      <Route
                        path={`groups/*`}
                        element={userPermissionsSet.has(PermissionsConstants.ManageGroups) ? <GroupsPage /> : <UnauthorizedPage />}
                      />
                      <Route
                        path={`permissions/*`}
                        element={userPermissionsSet.has(PermissionsConstants.ManageDefaultRoles) ? <PermissionsPage /> : <UnauthorizedPage />}
                      />
                      <Route
                        path={`default-roles/*`}
                        element={userPermissionsSet.has(PermissionsConstants.ManagePermissions) ? <DefaultRolesPage /> : <UnauthorizedPage />}
                      />
                      <Route
                        path={`role-assignments/*`}
                        element={
                          userPermissionsSet.has(PermissionsConstants.ManageRoleAssignments) ||
                          userPermissionsSet.has(PermissionsConstants.ManageGlobalLicenses) ? (
                            <RoleAssignmentsPage />
                          ) : (
                            <UnauthorizedPage />
                          )
                        }
                      />
                      <Route
                        path={`audit-logs/*`}
                        element={userPermissionsSet.has(PermissionsConstants.ManageUsers) ? <AuditLogsPage /> : <UnauthorizedPage />}
                      />
                      <Route path={`dev-logs/*`} element={authResult?.email === 'admin@tempuri.org' ? <DevLogsPage /> : <UnauthorizedPage />} />
                      <Route
                        path={`licenses/*`}
                        element={userPermissionsSet.has(PermissionsConstants.ManageGlobalLicenses) ? <LicensesPage /> : <UnauthorizedPage />}
                      />
                      <Route
                        path={`statistics/*`}
                        element={
                          [PermissionsConstants.ManageAdminOrganization].some((x) => userPermissionsSet.has(x)) ? (
                            <StatisticsPage />
                          ) : (
                            <UnauthorizedPage />
                          )
                        }
                      />
                      <Route
                        path={`scheduled-events/*`}
                        element={
                          userPermissionsSet.has(PermissionsConstants.ManageAdminOrganization) ? <ScheduledEventsListPage /> : <UnauthorizedPage />
                        }
                      />
                      <Route
                        path={`video-tutorials/*`}
                        element={userPermissionsSet.has(PermissionsConstants.ManageVideoTutorialData) ? <TutorialsListPage /> : <UnauthorizedPage />}
                      />
                      <Route
                        path={`clients/*`}
                        element={userPermissionsSet.has(PermissionsConstants.CanManageClients) ? <ClientsListPage /> : <UnauthorizedPage />}
                      />
                      <Route path={`eula`} element={<EulaPage />} />
                      <Route path={`export-import-hangfire-dashboard`} element={<HangfireDashboard />} />
                      <Route path={`unauthorized`} element={<UnauthorizedPage />} />
                      <Route element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </NotificationsContext>
              )}
            </Shell>
          </OrganizationExportImportContext.Provider>
        </div>
      )}
    </SecureContent>
  );
};

export default AdminIndex;
